// src/components/OverViewsPage/VaccinationHistory/VaccinationHistory.jsx
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './VaccinationHistory.css';
import VaccinationIcon from '@/assets/icons/vaccination.png';
import ArrowIcon from '@/assets/icons/arroww.png';

/* Les ids backend sont des entiers auto-incrémentés. Un id local
   (`temp-…`) ne doit JAMAIS finir dans `backendId` : la sauvegarde s'en sert
   pour choisir PUT (existant) ou POST (nouveau), et un PUT sur un id local
   part en 404 — c'est ce qui empêchait tout ajout de vaccin. */
const isBackendId = (value) => /^\d+$/.test(String(value ?? '').trim());

let tempSeq = 0;
const nextTempId = () => `temp-${Date.now().toString(36)}-${(tempSeq += 1)}`;

const normalizeVaccinations = (arr) =>
  (Array.isArray(arr) ? arr : []).map((it, idx) => {
    const backendId = isBackendId(it?.backendId)
      ? it.backendId
      : isBackendId(it?.id)
      ? it.id
      : null;

    return {
      id: String(it?.id ?? backendId ?? `v-${idx}`),
      backendId,
      version: it?.version,
      label: String(it?.vaccine || it?.label || '').trim(),       // ← "vaccine" → label
      year:
        it?.year ??
        (it?.date ? new Date(it.date).getFullYear() : undefined), // ← "date" → year
    };
  });

const VaccinationHistory = ({ editable = false, onChange, initialItems }) => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!Array.isArray(initialItems)) return;
    // une liste vide doit vider la carte (avant, supprimer la dernière ligne
    // laissait l'ancienne liste affichée)
    setItems(normalizeVaccinations(initialItems));
  }, [initialItems]);

  const [newLabel, setNewLabel] = useState('');
  const [newYear, setNewYear] = useState('');
  const [addError, setAddError] = useState('');

  const pushChange = (next) => {
    setItems(next);
    onChange?.(next);
  };

  const handleEdit = (id, field, value) => {
    const next = items.map((it) =>
      it.id === id
        ? {
            ...it,
            [field]: field === 'year' ? value.replace(/[^\d]/g, '') : value,
          }
        : it
    );
    pushChange(next);
  };

  const handleDelete = (id) => {
    pushChange(items.filter((it) => it.id !== id));
  };

  const handleAdd = () => {
    const label = newLabel.trim();
    const yearNum = Number(newYear);
    if (!label) return;

    /* Année hors bornes : avant, `return` muet → le "+" ne faisait rien et
       l'utilisateur n'avait aucune explication. */
    if (newYear !== '' && (!yearNum || yearNum < 1900 || yearNum > 2100)) {
      setAddError(t('vaccinationHistory.invalidYear'));
      return;
    }

    const next = [
      ...items,
      {
        id: nextTempId(),
        backendId: null, // → sera CRÉÉ (POST) à l'enregistrement
        label,
        year: newYear === '' ? undefined : yearNum,
      },
    ];
    pushChange(next);
    setNewLabel('');
    setNewYear('');
    setAddError('');
  };

  /* Le brouillon de la ligne "ajouter" n'existait que dans cet état local :
     taper un nom puis cliquer directement sur "Save changes" le perdait
     silencieusement (rien n'était enregistré, aucune erreur). On le valide
     dès que le focus quitte le bloc d'ajout — sauf s'il reste à l'intérieur
     (tabulation vers l'année, clic sur "+"). */
  const commitDraftOnFocusOut = (event) => {
    if (event.currentTarget.contains(event.relatedTarget)) return;
    handleAdd();
  };

  return (
    <section className="vh-card">
      <div className="vh-head">
        <div className="vh-title-icon">
          <img src={VaccinationIcon} alt="" width={24} height={24} />
        </div>
        <h4 className="vh-title">{t('vaccinationHistory.title')}</h4>
      </div>
      <div className="vh-sep" />

      <div className="vh-subhead">
        <img src={ArrowIcon} alt="" width={12} height={12} />
        <span>{t('vaccinationHistory.latestVaccinations')}</span>
      </div>

      {!editable ? (
        <ul className="vh-list">
          {items.map((it) => (
            <li key={it.id}>
              <span className="dot" />
              <span className="text">
                {it.label} {it.year ? <span className="year">({it.year})</span> : null}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <div className="vh-edit">
          <ul className="vh-edit-list">
            {items.map((it) => (
              <li key={it.id} className="vh-edit-item">
                {/* dot supprimé intentionnellement en mode édition */}
                <input
                  className="vh-input vh-input--label"
                  type="text"
                  value={it.label}
                  onChange={(e) => handleEdit(it.id, 'label', e.target.value)}
                  placeholder={t('vaccinationHistory.vaccineName')}
                />
                <input
                  className="vh-input vh-input--year"
                  type="text"
                  inputMode="numeric"
                  value={String(it.year ?? '')}
                  onChange={(e) => handleEdit(it.id, 'year', e.target.value)}
                  placeholder={t('vaccinationHistory.yearPlaceholder')}
                  maxLength={4}
                />
                <button
                  className="vh-del-btn"
                  onClick={() => handleDelete(it.id)}
                  aria-label={t('vaccinationHistory.deleteButton')}
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>

          <div className="vh-add" onBlur={commitDraftOnFocusOut}>
            <input
              className="vh-input vh-input--label"
              type="text"
              placeholder={t('vaccinationHistory.addVaccinationPlaceholder')}
              value={newLabel}
              onChange={(e) => { setNewLabel(e.target.value); setAddError(''); }}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            />
            <input
              className="vh-input vh-input--year"
              type="text"
              inputMode="numeric"
              placeholder={t('vaccinationHistory.yearPlaceholder')}
              value={newYear}
              onChange={(e) => { setNewYear(e.target.value.replace(/[^\d]/g, '')); setAddError(''); }}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
              maxLength={4}
            />
            <button
              className="vh-add-btn"
              onClick={handleAdd}
              aria-label={t('vaccinationHistory.addButton')}
            >
              +
            </button>
          </div>

          {addError ? (
            <div className="vh-add-error" role="alert">
              {addError}
            </div>
          ) : null}
        </div>
      )}
    </section>
  );
};

export default VaccinationHistory;
